import { useEffect, useState } from 'react';
import { APP_STORE_URL } from '../../constants/storeUrls';
import { trackEvent, WEB_STORE_CLICK } from '../../lib/analytics';
import { isAndroid, isIos } from '../../lib/inAppBrowser';
import styles from './SmartAppBanner.module.css';

const DISMISS_KEY = 'luvlust_smart_banner_dismissed';

type MobilePlatform = 'ios' | 'android' | 'other';

function getMobilePlatform(): MobilePlatform {
  if (typeof navigator === 'undefined') return 'other';
  if (isIos()) return 'ios';
  if (isAndroid()) return 'android';
  return 'other';
}

function getCtaUrl(platform: MobilePlatform): string {
  if (platform === 'android') return '/get';
  return APP_STORE_URL;
}

export function SmartAppBanner() {
  const [visible, setVisible] = useState(false);
  const [platform, setPlatform] = useState<MobilePlatform>('other');

  useEffect(() => {
    if (sessionStorage.getItem(DISMISS_KEY)) return;
    setPlatform(getMobilePlatform());
    setVisible(true);
  }, []);

  if (!visible) return null;

  const storeUrl = getCtaUrl(platform);
  const storeLabel = 'App Store';
  const external = storeUrl.startsWith('http');

  function handleDismiss(e: React.MouseEvent) {
    e.preventDefault();
    e.stopPropagation();
    sessionStorage.setItem(DISMISS_KEY, '1');
    setVisible(false);
  }

  function handleClick() {
    void trackEvent(WEB_STORE_CLICK, {
      platform: platform === 'android' ? 'android' : 'ios',
      location: 'smart_banner',
    });
  }

  return (
    <a
      href={storeUrl}
      className={styles.banner}
      target={external ? '_blank' : undefined}
      rel={external ? 'noopener noreferrer' : undefined}
      aria-label={`Download LuvLust on the ${storeLabel}`}
      onClick={handleClick}
    >
      <span className={styles.avatars} aria-hidden="true">
        <span className={styles.avatar} />
        <span className={styles.avatar} />
        <span className={styles.avatar} />
      </span>
      <span className={styles.copy}>
        Loved by <strong>500k+</strong> couples with <strong>4.8★</strong> rating
      </span>
      <span className={styles.cta}>Get on {storeLabel}</span>
      <button
        type="button"
        className={styles.dismiss}
        aria-label="Dismiss download banner"
        onClick={handleDismiss}
      >
        ×
      </button>
    </a>
  );
}
